import { CSSResultGroup, TemplateResult, html } from 'lit';
import { IdbStyledText } from '../idb-styled-text/idb-styled-text';
import { customElement, property } from 'lit/decorators.js';

type EllipsedOptions = {
  replaceStr: string;
  responsive: boolean;
  debounceDelay: number;
  delimiter: string;
  forceFirstUpdate: boolean;
};

type Acc = {
  el: HTMLElement;
  elStyle: CSSStyleDeclaration;
  elHeight: string | number;
  rowsLimit: number;
  rowsWrapped: number;
  buffer: string;
  options: EllipsedOptions;
};

/**
 * The IdbTruncateText component
 * @element idb-truncate-text
 *
 * This component is intended to only be used on content that is
 * linking to pages or components that contain the entire text
 *
 * The `idb-styled-component` should not be used on page headers, titles,
 * labels, error messages, validation messages, or notifications.
 * This component is an accessible wrapper for the
 * {@link https://github.com/nzambello/ellipsed/blob/master/README.md ellipsed.js} library,
 * which allows us to truncate content to a fixed number of lines.
 * This library is missing some accessibility considerations and truncating text is inherently inaccessible.
 * The `idb-truncate-text`component adds the appropriate labels, tooltips, and keyboard navigation to the truncated elements.
 * This allows the content to be read by screen readers and navigated with the keyboard.
 *
 * It is still important to be aware of how you are using this component.
 * In general it is better to use a flex layout, character limit, or editor training than to truncate the text.
 * However, if truncating text be sure to not obscure important or helpful information and it
 * should link to a source that provides the full context
 *
 * @slot - The content to be truncated. Content should be wrapped in an element tag.
 */
@customElement('idb-truncate-text')
export class IdbTruncateText extends IdbStyledText {
  static styles: CSSResultGroup = [IdbStyledText.styles];

  /**
   * How many lines of text to show when truncating.
   * Requires truncate to be true.
   */
  @property({ type: Number, attribute: 'truncate-lines' })
  truncateLines = 3;

  /**
   * The string that substituted following the truncated string.
   */
  @property({ type: String, attribute: 'replace-string' })
  replaceString = '...';

  /**
   * If true, the method is re-called on resize event, so the number of rows is preserved.
   */
  @property({ type: Boolean, attribute: 'responsive' })
  responsive = true;

  /**
   * If responsive option is set, it's the delay value for resizing events debouncing.
   */
  @property({ type: Number, attribute: 'debounce-delay' })
  debounceDelay = 250;

  /**
   * Use '' for text written in languages such as Japanese, Chinese, etc. since
   * there's usually no space break in such languages;
   * or for text contains long word (word that is longer than the width of the container).
   */
  @property({ type: String, attribute: 'delimiter' })
  delimiter = ' ';

  /**
   * Forces the resize event after instantiation. This is necessary for components
   * that use the slotController with shadow shift to truncate on initial load
   */
  @property({ type: Boolean, attribute: 'force-first-update' })
  forceFirstUpdate = false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  firstUpdated(_changedProperties: Map<string, any>): void {
    super.firstUpdated(_changedProperties);
    this.truncateText();
    if (this.forceFirstUpdate) {
      window.dispatchEvent(new Event('resize'));
    }
    this.requestUpdate();
  }

  render(): TemplateResult {
    return html`<slot></slot>`;
  }

  /**
   * Queries for all the slotted elements that contain the clamped class and truncates them
   */
  truncateText(): void {
    const clampTextElements = this.children;
    const textArr = Array.from(clampTextElements) as HTMLElement[];

    textArr.forEach(clampText => {
      if (clampText !== null) {
        this.setAccessibleProperties(clampText);
        this.ellipsis(clampText, this.truncateLines, {
          responsive: this.responsive,
          delimiter: this.delimiter,
          debounceDelay: this.debounceDelay,
          replaceStr: this.replaceString,
          forceFirstUpdate: this.forceFirstUpdate,
        });
      }
    });
  }

  /**
   * Sets accessible properties on the element to account for truncation
   */
  setAccessibleProperties(el: HTMLElement): void {
    if (el !== null) {
      const screenReaderFullTextEl = document.createElement('div');
      screenReaderFullTextEl.classList.add('sr-only', 'visually-hidden');
      screenReaderFullTextEl.append(el.innerText);

      // adds full text as .sr-only element for screenreaders
      this.appendChild(screenReaderFullTextEl);
      // sets a title attribute to display the full text when the user hovers over the element
      el.setAttribute('title', el.innerText);
      // sets a tabindex to allow the element to be navigated with the keyboard
      el.setAttribute('tabindex', '0');
    }
  }

  tokensReducer(acc: Acc, token: string) {
    const { el, elStyle, elHeight, rowsLimit, rowsWrapped, options } = acc;
    const oldBuffer = acc.buffer;
    let newBuffer = oldBuffer;

    if (rowsWrapped === rowsLimit + 1) {
      return { ...acc };
    }
    const textBeforeWrap = oldBuffer;
    let newRowsWrapped = rowsWrapped;
    let newHeight = elHeight;
    el.innerHTML = newBuffer = oldBuffer.length
      ? `${oldBuffer}${options.delimiter}${token}${options.replaceStr}`
      : `${token}${options.replaceStr}`;

    if (parseFloat(elStyle.height) > parseFloat(elHeight as string)) {
      newRowsWrapped++;
      newHeight = elStyle.height;

      if (newRowsWrapped === rowsLimit + 1) {
        el.innerHTML = newBuffer =
          textBeforeWrap[textBeforeWrap.length - 1] === '.' &&
          options.replaceStr === '...'
            ? `${textBeforeWrap}..`
            : `${textBeforeWrap}${options.replaceStr}`;

        return { ...acc, elHeight: newHeight, rowsWrapped: newRowsWrapped };
      }
    }

    el.innerHTML = newBuffer = textBeforeWrap.length
      ? `${textBeforeWrap}${options.delimiter}${token}`
      : `${token}`;

    return {
      ...acc,
      buffer: newBuffer,
      elHeight: newHeight,
      rowsWrapped: newRowsWrapped,
    };
  }

  ellipsis = (
    selector: HTMLElement | NodeList,
    rows: number,
    options: EllipsedOptions
  ) => {
    const defaultOptions = {
      replaceStr: '...',
      responsive: false,
      debounceDelay: 250,
      delimiter: ' ',
      forceFirstUpdate: true,
    };

    const opts = { ...defaultOptions, ...options };

    const elements =
      selector && selector instanceof NodeList ? selector : [selector];
    const elArr = Array.from(elements) as HTMLElement[];

    const originalTexts: string[] = [];

    for (let i = 0; i < elArr.length; i++) {
      const el = elArr[i];
      originalTexts[i] = el.innerHTML;

      const splitText = el.innerHTML.split(opts.delimiter);

      el.innerHTML = '';
      const elStyle = window.getComputedStyle(el);

      splitText.reduce(this.tokensReducer, {
        el,
        buffer: el.innerHTML,
        elStyle,
        elHeight: 0,
        rowsLimit: rows,
        rowsWrapped: 0,
        options: opts,
      });
    }

    if (opts.responsive || opts.forceFirstUpdate) {
      let resizeTimeout: number | undefined;
      let last_window_w = window.innerWidth;

      const resizeHandler = () => {
        if (window.innerWidth !== last_window_w || opts.forceFirstUpdate) {
          last_window_w = window.innerWidth;

          for (let i = 0; i < elements.length; i++) {
            (elements[i] as HTMLElement).innerHTML = originalTexts[i];
          }

          this.ellipsis(selector, rows, { ...options, responsive: false });
          opts.forceFirstUpdate = false;
        }
      };

      const resizeListener = () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = window.setTimeout(resizeHandler, opts.debounceDelay);
      };

      window.addEventListener('resize', resizeListener);

      return resizeListener;
    } else {
      return;
    }
  };

  disableResponsive(listener: EventListener) {
    window.removeEventListener('resize', listener);
  }
}
